import React, { useState, useMemo } from "react";
import {
  TextInput,
  Button,
  Box,
  Spacer,
  Flex,
  Icon,
  H3,
  Modal,
  Select,
  DatePicker,
  P,
} from "orcs-design-system";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import moment from "moment";
import { isEmpty, map } from "lodash";
import { DEFAULT_DATE_FORMAT } from "src/consts/global";
import { createPublicAPICredential } from "src/queries/mutations/apiKeys.graphql";
import { useWorkspaces } from "src/contexts/global/WorkspaceContext";

const READABLE_ENTITIES = [
  { label: "All", value: "all" },
  { label: "Workspaces", value: "workspaces" },
  { label: "Teams", value: "teams" },
  { label: "People", value: "people" },
  { label: "Memberships", value: "memberships" },
  { label: "Objectives", value: "objectives" },
  { label: "Tags", value: "tags" },
];
const WRITEABLE_ENTITIES = [{ label: "Teams", value: "teams" }];

// Utility function to create disabled options
const createDisabledOptions = (options, excludeValue) => {
  return options.map((option) => ({
    ...option,
    isDisabled: option.value !== excludeValue,
  }));
};

// Custom hook for managing select options with "All" functionality
const useSelectWithAll = (defaultOptions, initialValue = []) => {
  const [options, setOptions] = useState(defaultOptions);
  const [selectedValue, setSelectedValue] = useState(initialValue);

  const handleChange = (el) => {
    const selectedOptions = map(el, (option) => option.value);

    if (selectedOptions.includes("all")) {
      // If "All" is selected, disable other options and set only "All"
      const updatedOptions = createDisabledOptions(defaultOptions, "all");
      setOptions(updatedOptions);
      setSelectedValue([{ label: "All", value: "all" }]);
    } else {
      // Reset options to default state
      setOptions(defaultOptions);
      setSelectedValue(el);
    }
  };

  const reset = () => {
    setOptions(defaultOptions);
    setSelectedValue([]);
  };

  return {
    options,
    selectedValue,
    handleChange,
    reset,
    setSelectedValue,
  };
};

const CreateModal = ({
  visible,
  onHideModal,
  onCreateCredentials,
  initialWorkspaces = [],
  initialScopes = [],
}) => {
  const workspaces = useWorkspaces();

  // Convert workspaces to options format for Select component
  const defaultWorkspaceOptions = useMemo(() => {
    const options = map(workspaces, ({ name, id }) => ({
      label: name,
      value: id,
    }));
    // Add all workspace option
    options.unshift({
      label: "All",
      value: "all",
    });
    return options;
  }, [workspaces]);

  const [createPublicApiKey] = useMutation(createPublicAPICredential);
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(moment().add(1, "year"));
  const [selectedWriteScopes, setSelectedWriteScopes] = useState(initialScopes);
  const [createError, setCreateError] = useState(false);
  const [formFieldsError, setFormFieldsError] = useState(false);
  const [expiryDateError, setExpiryDateError] = useState(false);
  const today = moment().startOf("day");
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);

  // Use unified select hooks
  const {
    options: workspaceOptions,
    selectedValue: selectedWorkspaces,
    handleChange: handleWorkspaceChange,
    reset: resetWorkspaces,
  } = useSelectWithAll(defaultWorkspaceOptions, initialWorkspaces);

  const {
    options: readScopeOptions,
    selectedValue: selectedReadScopes,
    handleChange: handleReadScopesChange,
    reset: resetReadScopes,
  } = useSelectWithAll(READABLE_ENTITIES, initialScopes);

  const handleOnConfirm = async () => {
    const chosenWorkspaces = selectedWorkspaces.map(({ value }) => value);
    const chosenReadScopes = selectedReadScopes.map(({ value }) => value);
    const chosenWriteScopes = selectedWriteScopes.map(({ value }) => value);
    const chosenScopes = [...chosenReadScopes, ...chosenWriteScopes];
    const expiryDate = date?.utc().format("YYYY-MM-DD");
    const isFormInvalid =
      isEmpty(chosenWorkspaces) ||
      isEmpty(chosenScopes) ||
      isEmpty(description.trim()) ||
      isEmpty(expiryDate);

    if (isFormInvalid) {
      setFormFieldsError(true);
      return;
    }

    const isExpiryDateInvalid =
      expiryDate > moment().add(1, "year").toISOString();
    if (isExpiryDateInvalid) {
      setExpiryDateError(true);
      return;
    }

    setLoading(true);
    setFormFieldsError(false);
    setExpiryDateError(false);

    try {
      const { data } = await createPublicApiKey({
        variables: {
          workspaces: chosenWorkspaces,
          readScopes: chosenReadScopes,
          writeScopes: chosenWriteScopes,
          description: description.trim(),
          expiryDate,
        },
      });

      if (data?.apiKey) {
        setCreateError(false);
        onCreateCredentials(data.apiKey);
      }
    } catch (error) {
      setCreateError(true);
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    setCreateError(false);
    setSelectedWriteScopes([]);
    setDescription("");
    setDate(moment().add(1, "year"));
    setFormFieldsError(false);
    setExpiryDateError(false);
    setFocused(false);
    // Reset workspace and read scope selects
    resetWorkspaces();
    resetReadScopes();
    onHideModal();
  };

  const modalHeader = (
    <Box pt="r">
      <H3>Create API Key</H3>
    </Box>
  );

  const modalFooter = (
    <Flex>
      <Spacer mr="s">
        <Button
          onClick={handleOnConfirm}
          variant="success"
          iconLeft
          isLoading={loading}
        >
          <Icon icon={["fas", "save"]} />
          Create API Key
        </Button>
        <Button variant="ghost" onClick={onCancel} iconLeft disabled={loading}>
          <Icon icon={["fas", "times"]} />
          Cancel
        </Button>
      </Spacer>
    </Flex>
  );

  return (
    <Modal
      width="95vw"
      height="auto"
      maxWidth="450px"
      overflow="visible"
      visible={visible}
      handleOnConfirm={handleOnConfirm}
      onClose={() => {
        onCancel();
      }}
      headerContent={modalHeader}
      footerContent={modalFooter}
    >
      <Box pt="r">
        <Spacer mb="r">
          <Select
            options={workspaceOptions}
            mandatory
            isMulti
            label="Choose workspaces"
            onChange={handleWorkspaceChange}
            value={selectedWorkspaces}
          />
          <TextInput
            mandatory
            type="text"
            label="Description"
            maxLength={140}
            value={description}
            placeholder="140 characters max"
            onChange={(el) => {
              setDescription(el.target.value);
            }}
            fullWidth={true}
          />
        </Spacer>
        <Spacer mb="s">
          <Box mb="s">
            <P fontSize="1.2rem" mb="xs">
              Select scopes
              <small style={{ color: "rgb(217, 15, 39)", marginLeft: "2px" }}>
                *
              </small>
            </P>
          </Box>
          <Select
            options={readScopeOptions}
            isMulti
            label="Read"
            value={selectedReadScopes}
            onChange={handleReadScopesChange}
          />
          <Select
            options={WRITEABLE_ENTITIES}
            isMulti
            label="Write"
            value={selectedWriteScopes}
            onChange={(el) => {
              setSelectedWriteScopes(el);
            }}
          />
          <em style={{ fontSize: "1.2rem" }}>
            Scopes allow to you to choose what data the API key can access
          </em>
        </Spacer>

        <Spacer my="l">
          <DatePicker
            single
            openDirection="up"
            numberOfMonths={1}
            placeholder="Pick a date"
            focused={focused}
            date={date}
            onFocusChange={(el) => {
              setFocused(el.focused);
            }}
            onDateChange={(el) => {
              setDate(el);
            }}
            displayFormat={DEFAULT_DATE_FORMAT}
            label="Expiry Date"
            ariaLabel="Expiry Date"
            invalid={expiryDateError}
            required={true}
            isOutsideRange={(day) =>
              day.isBefore(today) || day.isAfter(moment().add(1, "year"))
            }
          />
        </Spacer>
        {expiryDateError && (
          <P mb="r" mt="r" style={{ color: "red" }}>
            Expiry date must be less than 1 year from now
          </P>
        )}
      </Box>
      {createError && (
        <P mb="r" mt="r" style={{ color: "red" }}>
          An error has occurred while creating credentials. Please try again.
        </P>
      )}
      {formFieldsError && (
        <P mb="r" mt="r" style={{ color: "red" }}>
          Please fill in all required fields
        </P>
      )}
    </Modal>
  );
};

CreateModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHideModal: PropTypes.func.isRequired,
  onCreateCredentials: PropTypes.func.isRequired,
  initialWorkspaces: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  initialScopes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
};

export default CreateModal;
